import React, { useMemo } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { COMPLIANCE_META, isComplianceFocus } from '../complianceFocus';
import type { CrewStatisticsResponse } from '../types';
import { StatsComplianceReport } from './StatsComplianceReport';
import { statsComplianceBackPath, statsComplianceListApiUrl } from './statsCompliancePrintMeta';

const StatsComplianceReportPage: React.FC = () => {
  const { colors } = useTheme();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const [searchParams] = useSearchParams();
  const focus = isComplianceFocus(focusParam) ? focusParam : null;

  const listUrl = useMemo(() => statsComplianceListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CrewStatisticsResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  if (!focus) {
    return <Navigate to="/crew/reports/compliance/certs-expired" replace />;
  }

  const meta = COMPLIANCE_META[focus];
  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const withinDays = searchParams.get('withinDays')?.trim() || String(data?.withinDays ?? 90);
  const vesselLabel = data?.vessel?.vesselName
    ? data.vessel.vesselName
    : !vesselId
      ? 'All vessels'
      : `Vessel #${vesselId}`;

  const lists = data?.lists;
  const certs = focus === 'certs-expired' ? lists?.certsExpired ?? [] : lists?.certsExpiring ?? [];
  const medicals =
    focus === 'medicals-expired' ? lists?.medicalsExpired ?? [] : lists?.medicalsExpiring ?? [];
  const coe = focus === 'coe-soon' ? lists?.coeSoon ?? [] : lists?.intentionOverdue ?? [];
  const nationalities = data?.charts.nationalityMix ?? [];
  const count =
    focus === 'certs-expired' || focus === 'certs-expiring'
      ? certs.length
      : focus === 'medicals-expired' || focus === 'medicals-due'
        ? medicals.length
        : focus === 'coe-soon' || focus === 'intention-overdue'
          ? coe.length
          : nationalities.length;

  const backTo = statsComplianceBackPath(focus);

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading {meta.title.toLowerCase()}…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load {meta.title.toLowerCase()}.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to {meta.title}
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backTo}
      backLabel={`Back to ${meta.title}`}
      title={meta.title}
      subtitle={`${vesselLabel} · ${count} records`}
      printPageSize="A4 portrait"
      printMargin="0"
    >
      <StatsComplianceReport
        focus={focus}
        vesselLabel={vesselLabel}
        withinDays={withinDays}
        certs={certs}
        medicals={medicals}
        coe={coe}
        nationalities={nationalities}
      />
    </ReportView>
  );
};

export default StatsComplianceReportPage;
